import { ref, watch } from 'vue';
import { STORAGE_KEYS, getStorageJSON, setStorageJSON } from '../storage';
// Default theme configuration (matching original config.min.js)
const defaultConfig = {
    theme: 'light',
    topbar: { color: 'light' },
    menu: { size: 'default', color: 'dark' }
};
// Shared reactive theme state (singleton pattern for global theme)
const theme = ref(defaultConfig.theme);
const topbarColor = ref(defaultConfig.topbar.color);
const menuColor = ref(defaultConfig.menu.color);
const menuSize = ref(defaultConfig.menu.size);
// Apply theme attributes to HTML element
const applyTheme = () => {
    if (typeof window === 'undefined')
        return;
    const html = document.documentElement;
    html.setAttribute('data-bs-theme', theme.value);
    html.setAttribute('data-topbar-color', topbarColor.value);
    html.setAttribute('data-menu-color', menuColor.value);
    // Handle menu size based on screen width (matching original behavior)
    if (window.innerWidth <= 1199) {
        html.setAttribute('data-menu-size', 'hidden');
    }
    else {
        html.setAttribute('data-menu-size', menuSize.value);
    }
};
// Initialize theme from localStorage or use defaults
// This matches the original config.min.js behavior
const initTheme = () => {
    if (typeof window === 'undefined')
        return;
    const html = document.documentElement;
    // Start with default config
    let config = JSON.parse(JSON.stringify(defaultConfig));
    // Check HTML attributes first (like original config does)
    const htmlTheme = html.getAttribute('data-bs-theme');
    const htmlTopbarColor = html.getAttribute('data-topbar-color');
    const htmlMenuColor = html.getAttribute('data-menu-color');
    const htmlMenuSize = html.getAttribute('data-menu-size');
    if (htmlTheme === 'light' || htmlTheme === 'dark')
        config.theme = htmlTheme;
    if (htmlTopbarColor === 'light' || htmlTopbarColor === 'dark')
        config.topbar.color = htmlTopbarColor;
    if (htmlMenuColor === 'light' || htmlMenuColor === 'dark')
        config.menu.color = htmlMenuColor;
    if (htmlMenuSize === 'default' || htmlMenuSize === 'compact' || htmlMenuSize === 'hidden') {
        config.menu.size = htmlMenuSize;
    }
    // Then check localStorage (original uses sessionStorage, but we'll use localStorage for persistence)
    const saved = getStorageJSON(STORAGE_KEYS.MANAGEMENT_CONFIG);
    if (saved) {
        config = {
            theme: saved.theme || config.theme,
            topbar: { color: saved.topbar?.color || config.topbar.color },
            menu: {
                size: saved.menu?.size || config.menu.size,
                color: saved.menu?.color || config.menu.color
            }
        };
    }
    // Update reactive refs
    theme.value = config.theme;
    topbarColor.value = config.topbar.color;
    menuColor.value = config.menu.color;
    menuSize.value = config.menu.size;
    // Apply theme to HTML element immediately (synchronously)
    applyTheme();
    // Ensure HTML and body don't have sidebar-enable class on init (sidebar should be visible by default)
    // This is critical - sidebar-enable class collapses the sidebar
    if (document.documentElement) {
        document.documentElement.classList.remove('sidebar-enable');
    }
    if (document.body) {
        document.body.classList.remove('sidebar-enable');
    }
    // Watch for window resize
    window.addEventListener('resize', () => {
        const html = document.documentElement;
        if (window.innerWidth <= 1199) {
            html.setAttribute('data-menu-size', 'hidden');
        }
        else {
            html.setAttribute('data-menu-size', menuSize.value);
        }
    });
};
// Initialize theme on module load (synchronously, before Vue mounts)
if (typeof window !== 'undefined') {
    // Wait for DOM to be ready, then initialize
    const initializeTheme = () => {
        // Ensure HTML and body don't have sidebar-enable class by default (sidebar should be visible)
        if (document.documentElement) {
            document.documentElement.classList.remove('sidebar-enable');
        }
        if (document.body) {
            document.body.classList.remove('sidebar-enable');
        }
        // Initialize theme immediately
        initTheme();
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeTheme);
    }
    else {
        // DOM is already ready
        initializeTheme();
    }
}
// Watch theme changes and apply automatically
watch([theme, topbarColor, menuColor, menuSize], () => {
    applyTheme();
}, { flush: 'post' });
export function useTheme() {
    // Save theme to localStorage
    const saveTheme = () => {
        if (typeof window === 'undefined')
            return;
        const config = {
            theme: theme.value,
            topbar: { color: topbarColor.value },
            menu: { size: menuSize.value, color: menuColor.value }
        };
        setStorageJSON(STORAGE_KEYS.MANAGEMENT_CONFIG, config);
    };
    // Toggle theme (light/dark)
    const toggleTheme = () => {
        theme.value = theme.value === 'dark' ? 'light' : 'dark';
        saveTheme();
    };
    // Set theme
    const setTheme = (newTheme) => {
        theme.value = newTheme;
        saveTheme();
    };
    // Set topbar color
    const setTopbarColor = (color) => {
        topbarColor.value = color;
        saveTheme();
    };
    // Set menu color
    const setMenuColor = (color) => {
        menuColor.value = color;
        saveTheme();
    };
    // Set menu size
    const setMenuSize = (size, save = true) => {
        menuSize.value = size;
        if (save) {
            saveTheme();
        }
        else {
            // Just apply the theme without saving (for temporary toggles)
            applyTheme();
        }
    };
    // Reset to defaults
    const resetTheme = () => {
        theme.value = defaultConfig.theme;
        topbarColor.value = defaultConfig.topbar.color;
        menuColor.value = defaultConfig.menu.color;
        menuSize.value = defaultConfig.menu.size;
        saveTheme();
    };
    return {
        theme,
        topbarColor,
        menuColor,
        menuSize,
        toggleTheme,
        setTheme,
        setTopbarColor,
        setMenuColor,
        setMenuSize,
        resetTheme,
        initTheme
    };
}
